import PersonIcon from '@mui/icons-material/Person';
import { Chip, Grid, ListItem, ListItemText, Tooltip, Typography, List, ListItemIcon } from '@mui/material';
import { isEmpty } from 'lodash';
import {
  DateField,
  FunctionField,
  Labeled,
  Link,
  NumberField,
  SimpleShowLayout,
  TextField,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { OfferOrderPaymentTypeDto } from 'src/dtos';
import { AccountStatusChip, FileDetails, OfferOrderStatusChip } from 'src/lib';
import { Account, OfferOrder, OfferOrderDetails } from 'src/models';
import { ColorConstant, Spacing } from 'src/styles';
import { getAccountHolderSubheader, getOfferOrderPaymentStatusColor } from 'src/utils';

import { OfferOrderRefundList } from './components/OfferOrderRefundList';
import { OfferOrderAccountHolder } from './OfferOrderAccountHolder';
import { OfferOrderTransferDetails } from './OfferOrderTransferDetails';
import { ResendOfferOrderEmailButton } from './ResendOfferOrderEmailButton';

export const OfferOrderDetailsTab = () => {
  const record = useRecordContext<OfferOrderDetails>();
  const { data: account } = useGetOne<Account>(DataProviderResource.Accounts, { id: record.account.id });

  return (
    <Grid container direction='row'>
      <Grid item xs={12} md={12}>
        <SimpleShowLayout>
          <Typography variant='h5'>Offer Order Details</Typography>
        </SimpleShowLayout>
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <SimpleShowLayout>
          <FunctionField
            source='offerName'
            label='Offer name'
            render={(record: OfferOrderDetails) => {
              return <Link to={`/Offers/${record.offerId}/show`}>{record?.offerName}</Link>;
            }}
          />
        </SimpleShowLayout>
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <SimpleShowLayout>
          <NumberField label='Quantity' source='quantity' />
        </SimpleShowLayout>
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <SimpleShowLayout>
          <NumberField label='Total cost' source='totalCost' options={{ style: 'currency', currency: 'USD' }} />
        </SimpleShowLayout>
      </Grid>
      {record.conditional && (
        <Grid item xs={12} sm={12} md={6}>
          <SimpleShowLayout>
            <NumberField
              label='Total requested investment'
              source='conditional.totalInvestment'
              options={{ style: 'currency', currency: 'USD' }}
            />
          </SimpleShowLayout>
        </Grid>
      )}
      <Grid item xs={12} sm={12} md={6}>
        <SimpleShowLayout>
          <FunctionField
            label='Status'
            render={(record: OfferOrder) => {
              if (!record) {
                return null;
              }

              return <OfferOrderStatusChip value={record.status} />;
            }}
          />
        </SimpleShowLayout>
      </Grid>
      <Grid item xs={6} sm={6} md={6}>
        <SimpleShowLayout>
          <FunctionField
            label='Payment type'
            render={(record: OfferOrder) => {
              if (record.paymentType.isCard || !isEmpty(record.refunds)) {
                return (
                  <div>
                    <span style={{ marginRight: '4px' }}>{record.paymentType.label}</span>
                    <Chip
                      size='small'
                      label={record.paymentStatus.label}
                      color={getOfferOrderPaymentStatusColor(record.paymentStatus.value)}
                    />
                  </div>
                );
              }

              return `${record.paymentType.label} ${
                record.retirementAccountFundingType?.value ? `(${record.retirementAccountFundingType.label})` : ''
              }`;
            }}
          />
        </SimpleShowLayout>
      </Grid>
      {record.isExternal && (
        <Grid item xs={6} sm={6} md={6}>
          <SimpleShowLayout>
            <FunctionField
              label='Security Delivery Information'
              render={(record: OfferOrderDetails) => {
                if (
                  record?.externalDetails?.securityDeliveryOption &&
                  record?.externalDetails?.securityDeliveryOption.tooltip
                ) {
                  const { label, tooltip } = record.externalDetails.securityDeliveryOption;
                  const startIndex = label.indexOf('<tooltip>');
                  const endIndex = label.indexOf('</tooltip>');
                  const beforeTooltip = label.substring(0, startIndex);
                  const betweenTooltip = label.substring(startIndex + '<tooltip>'.length, endIndex);
                  const afterTooltip = label.substring(endIndex + '</tooltip>'.length);

                  return (
                    <div>
                      {beforeTooltip}{' '}
                      <Tooltip title={tooltip}>
                        <span style={{ textDecoration: 'underline', color: ColorConstant.BRAND6 }}>
                          {betweenTooltip}
                        </span>
                      </Tooltip>{' '}
                      {afterTooltip}
                    </div>
                  );
                }

                return <>{record?.externalDetails?.securityDeliveryOption?.label ?? 'NA'}</>;
              }}
            />
          </SimpleShowLayout>
        </Grid>
      )}
      {record.paymentType.value === OfferOrderPaymentTypeDto.Card && (
        <>
          <Grid item xs={12} sm={12} md={6}>
            <SimpleShowLayout>
              <TextField label='Card payment intent id' source='cardPayment.intentId' />
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <SimpleShowLayout>
              <TextField label='Is card payment confirmed?' source='cardPayment.isConfirmed' />
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <SimpleShowLayout>
              <TextField label='Cardholder (billing name)' source='cardPayment.cardholder' />
            </SimpleShowLayout>
          </Grid>
        </>
      )}
      {(record.paymentType.isCard || !isEmpty(record.refunds)) && (
        <Grid item xs={12} sm={12} md={6}>
          <SimpleShowLayout>
            <FunctionField
              label='Payment Status'
              render={(record: OfferOrder) => {
                return (
                  <Chip
                    size='small'
                    label={record.paymentStatus.label}
                    color={getOfferOrderPaymentStatusColor(record.paymentStatus.value)}
                  />
                );
              }}
            />
          </SimpleShowLayout>
        </Grid>
      )}
      <Grid item xs={12} sm={12} md={6}>
        <SimpleShowLayout>
          <NumberField
            label='Total Amount Refunded'
            source='amountRefunded'
            options={{ style: 'currency', currency: 'USD' }}
          />
        </SimpleShowLayout>
      </Grid>
      <Grid container direction='row'>
        <Grid item xs={12} md={12}>
          <SimpleShowLayout>
            <Typography variant='h5'>Account Details</Typography>
          </SimpleShowLayout>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <SimpleShowLayout>
            <TextField label='Account id' source='accountId' />
          </SimpleShowLayout>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <SimpleShowLayout>
            <FunctionField
              label='Account number'
              render={(order: OfferOrder) => {
                return <Link to={`/Accounts/${order.account.id}/show`}>{order.account.accountNumber}</Link>;
              }}
              sortable={false}
            />
          </SimpleShowLayout>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <SimpleShowLayout>
            <TextField source='account.typeLabel' label='Account type' sortable={false} />
          </SimpleShowLayout>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <SimpleShowLayout>
            <FunctionField
              label='Account status'
              render={(order: OfferOrderDetails) => {
                if (order.account.status) {
                  return <AccountStatusChip value={order.account.status} />;
                }

                return null;
              }}
              sortable={false}
            />
          </SimpleShowLayout>
        </Grid>
      </Grid>
      {account?.primaryAccountHolder && (
        <Grid container direction='row'>
          <Grid item xs={12} md={12}>
            <SimpleShowLayout>
              <Typography variant='h5'>Account Holder(s)</Typography>
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={12}>
            <SimpleShowLayout>
              <Typography variant='h5'>{getAccountHolderSubheader({ isPrimary: true, type: account.type })}</Typography>
            </SimpleShowLayout>
            <OfferOrderAccountHolder record={account.primaryAccountHolder} />
          </Grid>
        </Grid>
      )}
      {account?.secondaryAccountHolder && (account.type.isJoint || account.type.isUgma || account.type.isEntity) && (
        <Grid item xs={12}>
          <SimpleShowLayout>
            <Typography variant='h5'>
              {getAccountHolderSubheader({ type: account.type, authorizedSigner: 2 })}
            </Typography>
          </SimpleShowLayout>
          <OfferOrderAccountHolder record={account.secondaryAccountHolder} />
        </Grid>
      )}
      {account?.otherAccountHolders?.map((holder, index) => (
        <Grid key={holder.id} item xs={12}>
          <SimpleShowLayout>
            <Typography variant='h5'>
              {getAccountHolderSubheader({ type: account.type, authorizedSigner: index + 3 })}
            </Typography>
          </SimpleShowLayout>
          <OfferOrderAccountHolder record={holder} />
        </Grid>
      ))}
      <Grid item xs={12} sm={12} md={12}>
        <SimpleShowLayout>
          <Typography variant='h5'>Refunds</Typography>
        </SimpleShowLayout>
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <OfferOrderRefundList />
      </Grid>
      {record.account.type?.isRetirement && record.transferDetails && <OfferOrderTransferDetails />}
      <Grid item xs={6} sm={6} md={6}>
        <SimpleShowLayout>
          <TextField label='Notes' source='notes' />
        </SimpleShowLayout>
      </Grid>
      <Grid item xs={6} sm={6} md={6}>
        <SimpleShowLayout>
          <FunctionField
            label='Representatives'
            render={(record: OfferOrderDetails) => {
              return (
                <List dense>
                  {record.representatives?.map(rep => (
                    <ListItem key={rep.id}>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText primary={`${rep.firstName} ${rep.lastName}`} secondary={rep.email} />
                    </ListItem>
                  ))}
                </List>
              );
            }}
          />
        </SimpleShowLayout>
      </Grid>
      <Grid item md={12} className={Spacing.my32}>
        <Grid container direction='row'>
          <Grid item md={12}>
            <SimpleShowLayout>
              <Typography variant='h6'>Email Confirmation Errors</Typography>
            </SimpleShowLayout>
          </Grid>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <FunctionField
                  label='Last Failed Send Confirmation Email Error'
                  render={() => {
                    return (
                      <Grid container direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                        <Grid item md={11} className={Spacing.mt8}>
                          <FunctionField
                            render={(record: OfferOrderDetails) => record.lastFailedSendConfirmationEmailError ?? 'NA'}
                          />
                        </Grid>
                        <Grid md={1} item>
                          <ResendOfferOrderEmailButton
                            id={record.id}
                            confirmationEmail
                            disabled={!record.lastFailedSendConfirmationEmailError}
                          />
                        </Grid>
                      </Grid>
                    );
                  }}
                />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <FunctionField
                  label='Last Failed Send Confirmation Email Attempt At'
                  render={(record: OfferOrderDetails) => {
                    return record.lastFailedSendConfirmationEmailAttemptAt ? (
                      <DateField source='lastFailedSendConfirmationEmailAttemptAt' />
                    ) : (
                      'NA'
                    );
                  }}
                />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
        </Grid>
      </Grid>
      <Grid item md={12} className={Spacing.my32}>
        <Grid container direction='row'>
          <Grid item md={12}>
            <SimpleShowLayout>
              <Typography variant='h6'>Email Oversubscription Errors</Typography>
            </SimpleShowLayout>
          </Grid>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <FunctionField
                  label='Last Failed Send Oversubscription Email Error'
                  render={() => {
                    return (
                      <Grid container direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                        <Grid item md={11} className={Spacing.mt8}>
                          <FunctionField
                            render={(record: OfferOrderDetails) =>
                              record.lastFailedSendOversubscriptionEmailError ?? 'NA'
                            }
                          />
                        </Grid>
                        <Grid md={1} item>
                          <ResendOfferOrderEmailButton
                            id={record.id}
                            oversubscriptionEmail
                            disabled={!record.lastFailedSendOversubscriptionEmailError}
                          />
                        </Grid>
                      </Grid>
                    );
                  }}
                />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <FunctionField
                  label='Last Failed Send Oversubscription Email Attempt At'
                  render={(record: OfferOrderDetails) => {
                    return record.lastFailedSendOversubscriptionEmailAttemptAt ? (
                      <DateField source='lastFailedSendOversubscriptionEmailAttemptAt' />
                    ) : (
                      'NA'
                    );
                  }}
                />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
        </Grid>
      </Grid>
      {record.subscriptionAgreementFilename && (
        <>
          <Grid item md={12}>
            <SimpleShowLayout>
              <Typography variant='h5'>Subscription Agreement</Typography>
            </SimpleShowLayout>
          </Grid>
          <FileDetails
            key={record.subscriptionAgreementFilename}
            id={record.subscriptionAgreementFilename}
            name={record.subscriptionAgreementFilename}
            resource={{
              name: DataProviderResource.DownloadOfferOrderSubscriptionAgreement,
              params: {
                id: record.id,
                meta: {
                  accountId: record.account.accountId,
                  name: record.subscriptionAgreementFilename,
                },
              },
            }}
          />
        </>
      )}
    </Grid>
  );
};
